import { GMXCarousel } from '../../core/carousel/carousel'
import { sanitizeInput, sanitizeUrl } from '../../../utils/sanitize'
import { calculateImwidthForImage } from '../../../utils/calculateImwidthForImage'
import { getEntryPresetParams } from '../../../utils/entry-presets'

import {
	VehicleModel,
	VehicleModelImageModel,
	VehicleModelLinksModel,
	VehicleIdData,
	VehicleModelExtraTextModel,
} from '../../../models/vehicle-model.model'

interface CTA {
	ctaKey: string
	label: string
	icon: string
}

/**
 * Carousel - Vehicle Model
 */
export class GMXCarouselVehicleModel extends HTMLElement {
	modelScrollerContainerEl: HTMLElement
	modelInfoContainerEl: HTMLElement
	modelNameEl: HTMLElement
	modelDescEl: HTMLElement
	modelPriceEl: HTMLElement
	modelAsShownPriceEl: HTMLElement
	modelStartingAtPriceEl: HTMLElement
	ctaContainerEl: HTMLElement
	carouselControlsEl: HTMLElement

	// temp
	carousel: GMXCarousel
	carouselSlides: NodeListOf<HTMLElement>

	/**
	 * Holds the data for the trims
	 * @private
	 */
	private modelsJSON: VehicleModel[]

	private activeModelId: string

	private boundCarouselTransitionEndHandler = this._handleCarouselTransitionEnd.bind(this)
	private boundCarouselControlsClickHandler = this._handleCarouselControlsClick.bind(this)

	get labelData(): Record<string, string> {
		return this.hasAttribute('data-labels') ? JSON.parse(this.getAttribute('data-labels')) : {}
	}

	get modelApiEndpoint(): string {
		return this.getAttribute('data-models-api-endpoint')
	}

	get ctaLinks(): CTA[] {
		return this.hasAttribute('data-ctas') ? JSON.parse(this.getAttribute('data-ctas')) : []
	}

	get modelsFilter(): VehicleIdData[] {
		let models: VehicleIdData[] = []

		try {
			models = JSON.parse(this.getAttribute('data-models'))
		} catch (error) {
			window.console.error(`ERROR:  Vehicle Carousel Model - Failed to get models: ${error}`)
		}

		return models
	}

	get priceDisclosureLink(): string {
		return this.getAttribute('data-price-disclosure-link') || ''
	}
	set priceDisclosureLink(value: string) {
		this.setAttribute('data-price-disclosure-link', value)
	}

	async connectedCallback(): Promise<void> {
		this.modelScrollerContainerEl = this.querySelector('.model-scroller-container')
		this.modelInfoContainerEl = this.querySelector('.model-info-container')
		this.modelNameEl = this.querySelector('.model-name')
		this.modelDescEl = this.querySelector('.model-description')
		this.modelPriceEl = this.querySelector('.model-price')
		this.ctaContainerEl = this.querySelector('.cta-container')
		this.carouselControlsEl = this.querySelector('.gmx-headline-controls')

		this.modelsJSON = await this.getModelData(this.modelApiEndpoint)

		if (this.modelsJSON?.length) {
			this.buildCarousel()

			// Preset the initial in-view model from a `?model=` entry parameter, if it matches a slide.
			this._preselectModelFromEntryParams()

			this._handleCarouselTransitionEnd()

			if (this.carouselControlsEl) {
				this.carouselControlsEl.addEventListener('click', this.boundCarouselControlsClickHandler)
			}
		}
	}

	/**
	 * Presets the carousel to the slide matching the `?model=` entry parameter (by stable model code),
	 * without reordering the carousel. Falls back to default behavior when the param is absent or
	 * does not resolve to a slide.
	 */
	private _preselectModelFromEntryParams(): void {
		const presetModel = getEntryPresetParams().model

		if (!presetModel || !this.carousel) {
			return
		}

		const targetIndex = Array.from(this.carouselSlides).findIndex(
			slide => slide.getAttribute('data-model-id') === presetModel
		)

		if (targetIndex > 0) {
			this.carousel.goToSlide(targetIndex)
		}
	}

	disconnectedCallback(): void {
		if (this.carousel) {
			this.carousel.removeEventListener('transitionend', this.boundCarouselTransitionEndHandler)
		}

		if (this.carouselControlsEl) {
			this.carouselControlsEl.removeEventListener('click', this.boundCarouselControlsClickHandler)
		}
	}

	/**
	 * Callback to handle when the carousel controls (prev & next buttons/arrows) are clicked to move the carousel backwards or forwards.
	 * @param e
	 */
	private _handleCarouselControlsClick(e: Event): void {
		const target = e.target as HTMLElement

		if (target.classList.contains('gmx-prev-ctrl')) {
			this.carousel.activatePreviousSlide()
		} else if (target.classList.contains('gmx-next-ctrl')) {
			this.carousel.activateNextSlide()
		}
	}

	/**
	 * Build Carousel
	 */
	buildCarousel(): void {
		this.carousel = document.createElement('gmx-carousel') as GMXCarousel
		this.carousel.setAttribute('data-offset-w-peek', 'true')

		// First, build the slide collection for the carousel
		const carouselSlideCollectionEl = document.createElement('div')
		carouselSlideCollectionEl.classList.add('slides-collection')

		this.modelsJSON.forEach((modelData: VehicleModel) => {
			const defaultImg: VehicleModelImageModel =
				modelData.images.filter(img => img.imageID === 'carouselVehicleModel')[0] || modelData.images[0] || null

			const newSlideEl = document.createElement('div')
			newSlideEl.classList.add('slide')
			newSlideEl.setAttribute('data-model-id', modelData.code)
			newSlideEl.setAttribute('data-as-shown-price', defaultImg.imageAsShownPrice || '')

			const slideWidth = this.offsetWidth * 0.8 // Using 80% of the tile width for the image to allow room for peek and prevent loading unnecessarily large images
			const safeImgUrl = defaultImg?.imageURL ? sanitizeUrl(defaultImg.imageURL) : ''
			let slideImgUrl = ''
			if (safeImgUrl) {
				const url = new URL(safeImgUrl)
				url.searchParams.set('imwidth', `${calculateImwidthForImage(slideWidth)}`)
				slideImgUrl = url.toString()
			}

			const newSlideImgEl = document.createElement('img')
			newSlideImgEl.src = slideImgUrl
			newSlideImgEl.alt = sanitizeInput(modelData.name)

			newSlideEl.appendChild(newSlideImgEl)
			carouselSlideCollectionEl.appendChild(newSlideEl)
		})

		this.carousel.innerHTML = ''
		this.carousel.appendChild(carouselSlideCollectionEl)

		this.modelScrollerContainerEl.appendChild(this.carousel)
		this.carouselSlides = this.carousel.querySelectorAll('.slide')

		this.carousel.addEventListener('transitionend', this.boundCarouselTransitionEndHandler)
	}

	/**
	 * Callback to handle when the Carousel transition end animation finishes.  Need to update display data
	 * based on which 'slide' is in view
	 * @private
	 */
	private _handleCarouselTransitionEnd(): void {
		const componentClientRect = this.getBoundingClientRect()

		/* Loop through slides and find the one that fits within the tile */
		this.carouselSlides.forEach(slide => {
			const slideClientRect = slide.getBoundingClientRect()

			if (
				slideClientRect.left >= componentClientRect.left &&
				slideClientRect.right <= componentClientRect.right
			) {
				const slideModelId = slide.getAttribute('data-model-id')

				if (slideModelId !== this.activeModelId) {
					const currentModelInfo = this.modelInfoContainerEl.querySelector('.model-info') as HTMLElement
					const oldModelInfoContainer = currentModelInfo.cloneNode(true) as HTMLElement
					oldModelInfoContainer.classList.add('prev-model')
					currentModelInfo.after(oldModelInfoContainer)

					const activeModel = this.modelsJSON.filter(m => m.code === slideModelId)[0]
					this.activeModelId = slideModelId

					this.modelNameEl.innerText = `${activeModel.year} ${activeModel.name}`
					const modelTagline =
						activeModel.extraText.filter(m => m.extraTextKey === 'modelTagline')[0]?.extraTextValue || ''
					this.modelDescEl.innerHTML = window.gmxDOMPurify?.sanitize
						? window.gmxDOMPurify.sanitize(modelTagline)
						: sanitizeInput(modelTagline)

					this.modelAsShownPriceEl = this.modelPriceEl.querySelector('.model-as-shown')

					// Build starting at price with disclosure
					if (activeModel.price && activeModel.price != '') {
						const startingAtPriceDisclosureEl = document.createElement('span')
						startingAtPriceDisclosureEl.setAttribute('data-legal-uri', this.priceDisclosureLink)
						startingAtPriceDisclosureEl.textContent = activeModel.price

						if (this.modelPriceEl.querySelector('.model-starting-at')) {
							this.modelStartingAtPriceEl = this.modelPriceEl.querySelector('.model-starting-at')
						} else {
							this.modelStartingAtPriceEl = document.createElement('span')
							this.modelStartingAtPriceEl.classList.add('model-starting-at')
							if (this.modelAsShownPriceEl) {
								this.modelPriceEl.insertBefore(this.modelStartingAtPriceEl, this.modelAsShownPriceEl)
							} else {
								this.modelPriceEl.appendChild(this.modelStartingAtPriceEl)
							}
						}

						this.modelStartingAtPriceEl.innerHTML = ''
						this.modelStartingAtPriceEl.append(
							`${sanitizeInput(this.labelData?.i18n_startingAt) || ''} `,
							startingAtPriceDisclosureEl
						)
					} else {
						if (this.modelStartingAtPriceEl) this.modelStartingAtPriceEl.remove()
					}

					// Build as shown price with disclosure; As shown price is tied to image and isn't in the same model location as 'starting at'
					const asShownPrice = slide.getAttribute('data-as-shown-price') || ''

					if (asShownPrice && asShownPrice != '') {
						const asShownPriceDisclosureEl = document.createElement('span')
						asShownPriceDisclosureEl.setAttribute('data-legal-uri', this.priceDisclosureLink)
						asShownPriceDisclosureEl.textContent = asShownPrice

						if (!this.modelAsShownPriceEl) {
							this.modelAsShownPriceEl = document.createElement('span')
							this.modelAsShownPriceEl.classList.add('model-as-shown')
							this.modelPriceEl.appendChild(this.modelAsShownPriceEl)
						}

						this.modelAsShownPriceEl.innerHTML = ''
						this.modelAsShownPriceEl.append(
							`${sanitizeInput(this.labelData?.i18n_asShown) || ''} `,
							asShownPriceDisclosureEl
						)
					} else {
						if (this.modelAsShownPriceEl) this.modelAsShownPriceEl.remove()
					}

					this._buildSlideCTAList(activeModel.links)

					currentModelInfo.addEventListener(
						'animationend',
						() => {
							currentModelInfo.classList.remove('animate')
						},
						{ once: true }
					)

					oldModelInfoContainer.addEventListener(
						'animationend',
						() => {
							this.modelInfoContainerEl.removeChild(oldModelInfoContainer)
						},
						{ once: true }
					)
					oldModelInfoContainer.classList.add('animate')
					currentModelInfo.classList.add('animate')
				}
			}
		})
	}

	/**
	 * Build CTA Markup for Model Slide.  Also appends it to the cta container element.
	 * @private
	 */
	private _buildSlideCTAList(modelCTAs: VehicleModelLinksModel[]): void {
		this.ctaContainerEl.innerHTML = ''

		// Get the number of matched CTAs so we know if to use a secondary or tertiary button for CTAs beyond the first
		const numMatchedCTAs = modelCTAs.filter(cta => this.ctaLinks.map(cl => cl.ctaKey).includes(cta.linkID)).length

		let numCTAsAdded = 0
		this.ctaLinks.forEach((link: CTA) => {
			const modelLink = modelCTAs.filter(cta => cta.linkID === link.ctaKey)[0]

			if (modelLink) {
				const ctaEl: HTMLButtonElement | HTMLAnchorElement =
					modelLink.primaryLink_type === 'fragment'
						? document.createElement('button')
						: document.createElement('a')
				ctaEl.classList.add(
					'gmx-btn',
					numCTAsAdded === 0
						? 'gmx-btn-primary'
						: numMatchedCTAs === 2
							? 'gmx-btn-secondary'
							: 'gmx-btn-tertiary'
				)

				ctaEl.textContent = modelLink.primaryLink_labelOverride || link.label || 'Label Missing'

				if (link.icon) {
					ctaEl.classList.add('icon-left')
					ctaEl.dataset.iconName = link.icon
					ctaEl.dataset.iconPosition = 'before'
				}

				switch (modelLink.primaryLink_type) {
					case 'internal':
						;(ctaEl as HTMLAnchorElement).href = encodeURI(`${modelLink.primaryLink_internalUrl}`)
						;(ctaEl as HTMLAnchorElement).target = sanitizeInput(
							modelLink.primaryLink_internalTarget || '_self'
						)
						break
					case 'external':
						;(ctaEl as HTMLAnchorElement).href = encodeURI(`${modelLink.primaryLink_externalUrl}`)
						;(ctaEl as HTMLAnchorElement).target = sanitizeInput(
							modelLink.primaryLink_externalTarget || '_blank'
						)
						break
					case 'relative':
						;(ctaEl as HTMLAnchorElement).href = encodeURI(`${modelLink.primaryLink_relativeUrl}`)
						;(ctaEl as HTMLAnchorElement).target = sanitizeInput(
							modelLink.primaryLink_relativeTarget || '_self'
						)
						break
					case 'anchor':
						;(ctaEl as HTMLAnchorElement).href = `#${sanitizeInput(modelLink.primaryLink_anchor)}`
						break
					case 'fragment':
						;(ctaEl as HTMLButtonElement).type = 'button'

						if (modelLink.primaryLink_xfType === 'expanded-tile') {
							// Is an overlay
							ctaEl.dataset.overlayRevealId = modelLink.primaryLink_xfPath
							ctaEl.dataset.overlayType = modelLink.primaryLink_xfType
						} else {
							// Is a panel
							ctaEl.dataset.panelRevealId = modelLink.primaryLink_xfPath
							ctaEl.dataset.panelType = modelLink.primaryLink_xfType

							if (ctaEl.dataset.panelType === 'form-panel' && modelLink.vehicleSelectorModelYear) {
								ctaEl.dataset.panelData = JSON.stringify({
									vehicleSelectorYear: modelLink.vehicleSelectorModelYear,
									vehicleSelectorModel: modelLink.vehicleSelectorModelName || '',
								})
							}
						}
						break
					default:
						break
				}

				this.ctaContainerEl.appendChild(ctaEl)

				numCTAsAdded++
			}
		})
	}

	/**
	 * Get all model data from a given endpoint/url.
	 * @param url
	 */
	async getModelData(url: string): Promise<VehicleModel[]> {
		let retModelData: VehicleModel[] = []

		try {
			const response = await fetch(url)

			if (response.ok) {
				const data = await response.json()
				const models = data?.data?.models

				if (this.modelsFilter?.length) {
					// Filter and sort models
					this.modelsFilter.forEach(model => {
						const modelData: VehicleModel | undefined = models.find(
							(m: VehicleModel) => m.code === model.vehicleId
						)

						if (modelData) {
							/* If we've got an overriding tagline,
							look for an existing modelTagline entry
							in the returned object. If it exists,
							update it; otherwise, add it. */
							if (model?.tagline !== undefined) {
								const existingTagline = modelData.extraText.find(e => e.extraTextKey === 'modelTagline')

								if (existingTagline) {
									existingTagline.extraTextValue = model.tagline
								} else {
									const taglineExtraText: VehicleModelExtraTextModel = {
										extraTextKey: 'modelTagline',
										extraTextValue: model.tagline,
									}

									modelData.extraText.push(taglineExtraText)
								}
							}

							retModelData.push(modelData)
						}
					})
				} else {
					// No models to filter, so just include them all
					retModelData = models
				}
			} else {
				throw new Error(`return status: ${response.status}`)
			}
		} catch (error) {
			window.console.error(`ERROR:  Failed to retrieve model data ${error}`)
		}

		return retModelData
	}
}

if (!customElements.get('gmx-carousel-vehicle-model')) {
	customElements.define('gmx-carousel-vehicle-model', GMXCarouselVehicleModel)
}
